package diana.soleil.design.iraniancanadienbazzar.core.data.fake

import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.AdminRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CampaignChannel
import diana.soleil.design.iraniancanadienbazzar.core.data.CampaignSendResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BlockRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CategoryRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ChatRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.EventRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ListingMetricsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.MarketplaceRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.NewsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.NotificationRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.OrderRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.OwnerRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ProductRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.PostRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ListingRequestRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.FollowRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReviewRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.TicketRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.UserPreferences
import diana.soleil.design.iraniancanadienbazzar.core.data.UserProfile
import diana.soleil.design.iraniancanadienbazzar.core.data.UserProfileRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.AuthUser
import diana.soleil.design.iraniancanadienbazzar.core.model.AppNotification
import diana.soleil.design.iraniancanadienbazzar.core.model.BlockedUser
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessClaim
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimRole
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimReviewStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Comment
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Category
import diana.soleil.design.iraniancanadienbazzar.core.model.Conversation
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.EventStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.NewsArticle
import diana.soleil.design.iraniancanadienbazzar.core.model.ItemCondition
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.ItemStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceItem
import diana.soleil.design.iraniancanadienbazzar.core.model.CartItem
import diana.soleil.design.iraniancanadienbazzar.core.model.DailyListingCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingMetric
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.Message
import diana.soleil.design.iraniancanadienbazzar.core.model.Order
import diana.soleil.design.iraniancanadienbazzar.core.model.OrderItem
import diana.soleil.design.iraniancanadienbazzar.core.model.OrderStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.PaymentStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import diana.soleil.design.iraniancanadienbazzar.core.model.Product
import diana.soleil.design.iraniancanadienbazzar.core.model.Deal
import diana.soleil.design.iraniancanadienbazzar.core.model.ModerationAction
import diana.soleil.design.iraniancanadienbazzar.core.model.PropertyDetails
import diana.soleil.design.iraniancanadienbazzar.core.model.Post
import diana.soleil.design.iraniancanadienbazzar.core.model.PostType
import diana.soleil.design.iraniancanadienbazzar.core.model.Report
import diana.soleil.design.iraniancanadienbazzar.core.model.ReportStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.DailyPlatformCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.UserAccount
import diana.soleil.design.iraniancanadienbazzar.core.model.Review
import diana.soleil.design.iraniancanadienbazzar.core.model.Ticket
import diana.soleil.design.iraniancanadienbazzar.core.model.TicketStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.TicketType
import diana.soleil.design.iraniancanadienbazzar.core.data.isUpcomingAt
import kotlin.time.Clock
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update

/**
 * In-memory repository doubles behind every repository interface. Two jobs:
 *  1. **Previews / platforms without Firebase** — the no-arg defaults are empty (or [FakeData]'s small
 *     directory), matching the historical behavior.
 *  2. **Mock-data mode** (Developer Menu → "Populate mock data") — `mockDataModule` constructs them
 *     seeded from [MockScenarios] and they behave statefully (send message, like a post, place an
 *     order, approve a claim…), so every flow is exercisable end-to-end with zero backend traffic.
 * All state is process-memory only; dropping the Koin graph (Clear mock data) discards everything.
 */
class FakeBusinessRepository(
    private val all: List<Business> = FakeData.businesses,
    override val currentUid: String? = null,
) : BusinessRepository {

    override fun observeBusinesses(
        metroId: String?,
        categoryId: String?,
        country: String?,
    ): Flow<DataResult<List<Business>>> =
        flowOf(DataResult.Success(all.filter { it.matches(metroId, categoryId, country) }))

    override fun observeFeatured(metroId: String?, country: String?): Flow<DataResult<List<Business>>> =
        flowOf(DataResult.Success(all.filter { it.matches(metroId, null, country) && it.sponsorship.active }))

    // Fixture order stands in for createdAt: the last seeded businesses are the "newest".
    override fun observeNewest(metroId: String?, country: String?, limit: Int): Flow<DataResult<List<Business>>> =
        flowOf(DataResult.Success(all.filter { it.matches(metroId, null, country) }.asReversed().take(limit)))

    override suspend fun getBusiness(id: String): DataResult<Business> =
        all.firstOrNull { it.id == id }?.let { DataResult.Success(it) }
            ?: DataResult.Failure(AppError.NotFound())

    override suspend fun getBusinesses(ids: Collection<String>): List<Business> =
        ids.mapNotNull { id -> all.firstOrNull { it.id == id } }

    override suspend fun submitBusiness(
        name: LocalizedText,
        categoryId: String,
        description: LocalizedText,
        addressLine: String,
        city: String,
        province: String,
        postalCode: String,
        metroId: String?,
        attributes: List<String>,
    ): DataResult<String> = DataResult.Success("fake-submitted-business")

    override suspend fun updateBusinessAttributes(businessId: String, attributes: List<String>): DataResult<Unit> =
        DataResult.Success(Unit)

    override suspend fun deleteBusiness(businessId: String): DataResult<Unit> = DataResult.Success(Unit)

    override suspend fun createPromotionCheckout(businessId: String, planId: String): DataResult<String> =
        DataResult.Success("https://checkout.stripe.com/fake/$businessId/$planId")

    override suspend fun updateBusinessPrivacy(
        businessId: String,
        hideExactAddress: Boolean,
        ownerHidden: Boolean,
    ): DataResult<Unit> = DataResult.Success(Unit)

    override suspend fun uploadBusinessMedia(
        businessId: String,
        cover: PickedImage?,
        logo: PickedImage?,
        gallery: List<PickedImage>,
    ): DataResult<Unit> = DataResult.Success(Unit)

    override suspend fun updateListingDetails(
        businessId: String,
        edit: diana.soleil.design.iraniancanadienbazzar.core.model.ListingDetailsEdit,
    ): DataResult<Unit> = DataResult.Success(Unit)

    override suspend fun removeBusinessPhoto(businessId: String, photoUrl: String): DataResult<Unit> =
        DataResult.Success(Unit)

    override suspend fun requestListingChange(
        business: Business,
        name: String?,
        categoryId: String?,
        address: diana.soleil.design.iraniancanadienbazzar.core.model.Address?,
    ): DataResult<Unit> = DataResult.Success(Unit)

    override fun observePendingListingChanges(
        businessId: String,
    ): Flow<DataResult<List<diana.soleil.design.iraniancanadienbazzar.core.model.ListingChangeRequest>>> =
        flowOf(DataResult.Success(emptyList()))

    override suspend fun requestClaim(businessId: String): DataResult<Unit> = DataResult.Success(Unit)

    override suspend fun submitClaim(
        businessId: String,
        businessName: String,
        claimantName: String,
        role: ClaimRole,
        contactEmail: String,
        contactPhone: String,
        message: String,
        proof: PickedImage?,
    ): DataResult<Unit> = DataResult.Success(Unit)

    private fun Business.matches(metroId: String?, categoryId: String?, country: String?): Boolean =
        (metroId == null || this.metroId == metroId) &&
            (categoryId == null || this.categoryId == categoryId) &&
            // Country only narrows the metro-null ("All <country>") case, mirroring the live query.
            (metroId != null || country == null || this.address.country.equals(country, ignoreCase = true))
}

/** In-memory [CategoryRepository] over [FakeData]. */
class FakeCategoryRepository(
    private val all: List<Category> = FakeData.categories,
) : CategoryRepository {
    override fun observeCategories(): Flow<DataResult<List<Category>>> =
        flowOf(DataResult.Success(all))
}

/** Stateful [ReviewRepository]: seeded reviews live-update; submits append. Empty by default. */
class FakeReviewRepository(
    seed: List<Review> = emptyList(),
    private val currentUid: String? = null,
    private val currentName: String = "Preview User",
) : ReviewRepository {
    private val reviews = MutableStateFlow(seed)

    override fun observeVisibleReviews(businessId: String): Flow<DataResult<List<Review>>> =
        reviews.map { all -> DataResult.Success(all.filter { it.businessId == businessId }) }

    override fun observeMyReviews(): Flow<DataResult<List<Review>>> =
        reviews.map { all -> DataResult.Success(all.filter { it.authorUid == currentUid }) }

    override suspend fun submitReview(
        businessId: String,
        rating: Int,
        comment: String,
        tags: List<String>,
    ): DataResult<Unit> {
        val uid = currentUid ?: "preview"
        reviews.update { all ->
            all.filterNot { it.id == "${uid}_$businessId" } + Review(
                id = "${uid}_$businessId",
                businessId = businessId,
                authorUid = uid,
                authorName = currentName,
                rating = rating,
                comment = comment,
                tags = tags,
                createdAtMillis = Clock.System.now().toEpochMilliseconds(),
            )
        }
        return DataResult.Success(Unit)
    }

    /** Mock-mode hook for [FakeOwnerRepository] so owner replies land on the same review state. */
    internal fun applyOwnerReply(reviewId: String, replyText: String) {
        reviews.update { all -> all.map { if (it.id == reviewId) it.copy(ownerReply = replyText) else it } }
    }
}

/** In-memory [FollowRepository] so previews/fakes can follow/unfollow and see it reflected live. */
class FakeFollowRepository : FollowRepository {
    private val following = MutableStateFlow<Set<String>>(emptySet())

    override fun observeFollowing(): Flow<DataResult<Set<String>>> =
        following.map { DataResult.Success(it) }

    override suspend fun setFollowing(businessId: String, follow: Boolean): DataResult<Unit> {
        following.value = following.value.toMutableSet().apply { if (follow) add(businessId) else remove(businessId) }
        return DataResult.Success(Unit)
    }
}

/** No-op [ReportRepository]. */
class FakeReportRepository : ReportRepository {
    override suspend fun fileReport(targetType: String, targetId: String, reason: String): DataResult<Unit> =
        DataResult.Success(Unit)
}

/** No-op [ListingRequestRepository]. */
class FakeListingRequestRepository : ListingRequestRepository {
    override suspend fun submit(
        businessId: String,
        businessName: String,
        type: diana.soleil.design.iraniancanadienbazzar.core.model.ListingRequestType,
        message: String,
        contactEmail: String?,
    ): DataResult<Unit> = DataResult.Success(Unit)
}

/** In-memory [ListingMetricsRepository]: counters live for the session; optionally seeded. */
class FakeListingMetricsRepository(
    seedCounters: Map<String, ListingCounters> = emptyMap(),
    private val seedDaily: Map<String, List<DailyListingCounters>> = emptyMap(),
) : ListingMetricsRepository {
    private val counters = MutableStateFlow(seedCounters)

    override suspend fun record(businessId: String, metric: ListingMetric) {
        val current = counters.value.getOrElse(businessId) { ListingCounters() }
        val next = when (metric) {
            ListingMetric.View -> current.copy(views = current.views + 1)
            ListingMetric.Call -> current.copy(calls = current.calls + 1)
            ListingMetric.Directions -> current.copy(directions = current.directions + 1)
            ListingMetric.Chat -> current.copy(chats = current.chats + 1)
            ListingMetric.Share -> current.copy(shares = current.shares + 1)
        }
        counters.value = counters.value + (businessId to next)
    }

    override fun observeCounters(businessId: String): Flow<DataResult<ListingCounters>> =
        counters.map { DataResult.Success(it.getOrElse(businessId) { ListingCounters() }) }

    override fun observeDailyCounters(
        businessId: String,
        sinceDay: String,
    ): Flow<DataResult<List<DailyListingCounters>>> =
        flowOf(DataResult.Success(seedDaily[businessId].orEmpty().filter { it.day >= sinceDay }))
}

/** Stateful [TicketRepository]: seeded types/wallet; free-ticket issuance appends. Empty by default. */
class FakeTicketRepository(
    private val types: Map<String, List<TicketType>> = emptyMap(),
    seedTickets: List<Ticket> = emptyList(),
    private val eventLookup: (String) -> Event? = { null },
) : TicketRepository {
    private val tickets = MutableStateFlow(seedTickets)

    override fun observeTicketTypes(eventId: String): Flow<DataResult<List<TicketType>>> =
        flowOf(DataResult.Success(types[eventId].orEmpty()))

    override fun observeMyTickets(): Flow<DataResult<List<Ticket>>> =
        tickets.map { DataResult.Success(it) }

    override suspend fun issueFreeTicket(eventId: String, ticketTypeId: String, qty: Int): DataResult<String> {
        val event = eventLookup(eventId)
        val id = "fake-ticket-${tickets.value.size + 1}"
        tickets.update { all ->
            all + Ticket(
                id = id,
                eventId = eventId,
                ticketTypeId = ticketTypeId,
                qty = qty,
                qrPayload = "MOCK-QR-$id",
                status = TicketStatus.Valid,
                eventTitle = event?.title ?: LocalizedText(),
                startsAtMillis = event?.startsAtMillis,
                venueName = event?.venue?.name.orEmpty(),
                purchasedAtMillis = Clock.System.now().toEpochMilliseconds(),
            )
        }
        return DataResult.Success(id)
    }
}

/** Stateful [ProductRepository]: seeded catalogs; owner upserts mutate. Empty by default. */
class FakeProductRepository(
    seed: Map<String, List<Product>> = emptyMap(),
) : ProductRepository {
    private val products = MutableStateFlow(seed)

    override fun observeProducts(businessId: String): Flow<DataResult<List<Product>>> =
        products.map { DataResult.Success(it[businessId].orEmpty()) }

    override suspend fun upsertProduct(
        businessId: String,
        productId: String?,
        name: LocalizedText,
        description: LocalizedText,
        priceCents: Long,
        available: Boolean,
        promoted: Boolean,
        property: PropertyDetails?,
    ): DataResult<Unit> {
        products.update { all ->
            val current = all[businessId].orEmpty()
            val id = productId ?: "fake-product-${current.size + 1}"
            val updated = Product(
                id = id, businessId = businessId, name = name, description = description,
                priceCents = priceCents, available = available, promoted = promoted, property = property,
                imageUrl = current.firstOrNull { it.id == id }?.imageUrl,
            )
            all + (businessId to current.filterNot { it.id == id } + updated)
        }
        return DataResult.Success(Unit)
    }
}

/** Stateful [OrderRepository]: seeded history; placing an order appends a Pending one. */
class FakeOrderRepository(
    seed: List<Order> = emptyList(),
) : OrderRepository {
    private val orders = MutableStateFlow(seed)

    override suspend fun placeOrder(items: List<CartItem>, note: String): DataResult<String> {
        val first = items.firstOrNull() ?: return DataResult.Failure(AppError.Unknown())
        val id = "fake-order-${orders.value.size + 1}"
        val subtotal = items.sumOf { it.lineTotalCents }
        orders.update { all ->
            listOf(
                Order(
                    id = id,
                    businessId = first.businessId,
                    businessName = first.businessName,
                    items = items.map { OrderItem(it.productId, it.name, it.quantity, it.priceCents) },
                    subtotalCents = subtotal,
                    totalCents = subtotal,
                    status = OrderStatus.Pending,
                    paymentStatus = PaymentStatus.Unpaid,
                    note = note,
                    createdAtMillis = Clock.System.now().toEpochMilliseconds(),
                ),
            ) + all
        }
        return DataResult.Success(id)
    }

    override fun observeMyOrders(): Flow<DataResult<List<Order>>> =
        orders.map { DataResult.Success(it) }

    override suspend fun getOrder(orderId: String): DataResult<Order> =
        orders.value.firstOrNull { it.id == orderId }?.let { DataResult.Success(it) }
            ?: DataResult.Failure(AppError.NotFound())

    override fun observeBusinessOrders(businessId: String): Flow<DataResult<List<Order>>> =
        orders.map { all -> DataResult.Success(all.filter { it.businessId == businessId }) }

    override suspend fun updateOrderStatus(
        orderId: String,
        status: OrderStatus?,
        paymentStatus: PaymentStatus?,
    ): DataResult<Unit> {
        orders.update { all ->
            all.map {
                if (it.id == orderId) it.copy(
                    status = status ?: it.status,
                    paymentStatus = paymentStatus ?: it.paymentStatus,
                ) else it
            }
        }
        return DataResult.Success(Unit)
    }

    override suspend fun cancelMyOrder(orderId: String): DataResult<Unit> =
        updateOrderStatus(orderId, status = OrderStatus.Cancelled)
}

/** Stateful [MarketplaceRepository]: seeded listings; create/edit/sold mutate. Empty by default. */
class FakeMarketplaceRepository(
    seed: List<MarketplaceItem> = emptyList(),
    override val currentUid: String? = null,
    private val currentName: String = "Preview User",
) : MarketplaceRepository {
    private val items = MutableStateFlow(seed)

    override fun observeActiveItems(metroId: String?): Flow<DataResult<List<MarketplaceItem>>> =
        items.map { all ->
            DataResult.Success(
                all.filter { it.status == ItemStatus.Active && (metroId == null || it.metroId == metroId) },
            )
        }

    override fun observeMyItems(): Flow<DataResult<List<MarketplaceItem>>> =
        items.map { all -> DataResult.Success(all.filter { it.sellerUid == currentUid }) }

    override suspend fun getItem(itemId: String): DataResult<MarketplaceItem> =
        items.value.firstOrNull { it.id == itemId }?.let { DataResult.Success(it) }
            ?: DataResult.Failure(AppError.NotFound())

    override suspend fun getItems(ids: Collection<String>): List<MarketplaceItem> =
        items.value.filter { it.id in ids }

    override suspend fun getActiveItemsBySeller(
        sellerUid: String,
        excludeItemId: String,
        limit: Int,
    ): DataResult<List<MarketplaceItem>> = DataResult.Success(
        items.value
            .filter { it.sellerUid == sellerUid && it.status == ItemStatus.Active && it.id != excludeItemId }
            .take(limit),
    )

    override suspend fun createItem(
        title: String,
        description: String,
        priceCents: Long?,
        isFree: Boolean,
        condition: ItemCondition,
        category: MarketplaceCategory,
        metroId: String?,
        city: String,
        photoUrls: List<String>,
    ): DataResult<String> {
        val newId = "fake-item-${items.value.size + 1}"
        items.update { all ->
            listOf(
                MarketplaceItem(
                    id = newId,
                    sellerUid = currentUid ?: "preview",
                    sellerName = currentName,
                    title = title,
                    description = description,
                    priceCents = priceCents,
                    isFree = isFree,
                    condition = condition,
                    category = category,
                    metroId = metroId,
                    city = city,
                    photoUrls = photoUrls,
                    createdAtMillis = Clock.System.now().toEpochMilliseconds(),
                ),
            ) + all
        }
        return DataResult.Success(newId)
    }

    override suspend fun updateItem(
        itemId: String,
        title: String,
        description: String,
        priceCents: Long?,
        isFree: Boolean,
        condition: ItemCondition,
        category: MarketplaceCategory,
        city: String,
    ): DataResult<Unit> {
        items.update { all ->
            all.map {
                if (it.id == itemId) {
                    it.copy(title = title, description = description, priceCents = priceCents, isFree = isFree, condition = condition, category = category, city = city)
                } else it
            }
        }
        return DataResult.Success(Unit)
    }

    override suspend fun markSold(itemId: String): DataResult<Unit> {
        items.update { all -> all.map { if (it.id == itemId) it.copy(status = ItemStatus.Sold) else it } }
        return DataResult.Success(Unit)
    }

    override suspend fun deleteItem(itemId: String): DataResult<Unit> {
        items.update { all -> all.filterNot { it.id == itemId } }
        return DataResult.Success(Unit)
    }

    override suspend fun addPhoto(itemId: String, image: PickedImage): DataResult<Unit> =
        DataResult.Success(Unit)
}

/** Seeded [EventRepository]: upcoming approved events per metro. Empty by default. */
class FakeEventRepository(
    private val all: List<Event> = emptyList(),
) : EventRepository {
    override fun observeUpcomingEvents(metroId: String?): Flow<DataResult<List<Event>>> =
        flowOf(
            DataResult.Success(
                // Same "upcoming" rule as the Firestore repository (isUpcomingAt) — the fake used to
                // return past events too, so mock mode and the tests agreed with each other and
                // disagreed with production.
                all.filter {
                    it.status == EventStatus.Approved &&
                        (metroId == null || it.metroId == metroId) &&
                        it.isUpcomingAt(Clock.System.now().toEpochMilliseconds())
                }.sortedBy { it.startsAtMillis },
            ),
        )

    override suspend fun getEvent(eventId: String): DataResult<Event> =
        all.firstOrNull { it.id == eventId }?.let { DataResult.Success(it) }
            ?: DataResult.Failure(AppError.NotFound())

    private val myRsvps = MutableStateFlow<Set<String>>(emptySet())

    override fun observeMyRsvps(): Flow<Set<String>> = myRsvps

    override suspend fun setRsvp(eventId: String, going: Boolean): DataResult<Unit> {
        myRsvps.update { if (going) it + eventId else it - eventId }
        return DataResult.Success(Unit)
    }

    override suspend fun rsvpCounts(eventIds: Collection<String>): Map<String, Int> =
        eventIds.associateWith { if (it in myRsvps.value) 1 else 0 }
}

/** Seeded news source (Home hides the section when empty). */
class FakeNewsRepository(
    private val all: List<NewsArticle> = emptyList(),
) : NewsRepository {
    override fun observeNews(metroId: String?, limit: Int): Flow<DataResult<List<NewsArticle>>> =
        flowOf(
            DataResult.Success(
                all.filter { metroId == null || it.metroId == null || it.metroId == metroId }
                    .sortedByDescending { it.publishedAtMillis }
                    .take(limit),
            ),
        )

    override suspend fun getArticle(articleId: String): DataResult<NewsArticle> =
        all.firstOrNull { it.id == articleId }?.let { DataResult.Success(it) }
            ?: DataResult.Failure(AppError.NotFound())
}

/** Stateful [NotificationRepository]: seeded rows; mark-read flips live (bell badge updates). */
class FakeNotificationRepository(
    seed: List<AppNotification> = emptyList(),
) : NotificationRepository {
    private val notifications = MutableStateFlow(seed)

    override fun observeNotifications(): Flow<DataResult<List<AppNotification>>> =
        notifications.map { DataResult.Success(it.sortedByDescending { n -> n.createdAtMillis ?: 0L }) }

    override suspend fun markConversationRead(conversationId: String): DataResult<Unit> {
        notifications.update { all ->
            all.map { if (it.conversationId == conversationId) it.copy(read = true) else it }
        }
        return DataResult.Success(Unit)
    }

    override suspend fun markRead(notificationId: String): DataResult<Unit> {
        notifications.update { all -> all.map { if (it.id == notificationId) it.copy(read = true) else it } }
        return DataResult.Success(Unit)
    }

    override suspend fun markAllRead(): DataResult<Unit> {
        notifications.update { all -> all.map { it.copy(read = true) } }
        return DataResult.Success(Unit)
    }

    override suspend fun registerDevice(token: String, language: String?): DataResult<Unit> =
        DataResult.Success(Unit)
}

/** Stateful [PostRepository]: seeded feed; posting/liking/saving/commenting all mutate live. */
class FakePostRepository(
    seedPosts: List<Post> = emptyList(),
    seedComments: Map<String, List<Comment>> = emptyMap(),
    override val currentUid: String? = null,
    private val currentName: String = "Preview User",
) : PostRepository {
    private val posts = MutableStateFlow(seedPosts)
    private val comments = MutableStateFlow(seedComments)
    private val liked = MutableStateFlow<Set<String>>(emptySet())
    private val saved = MutableStateFlow<Set<String>>(emptySet())

    override fun observePosts(metroId: String?, limit: Int): Flow<DataResult<List<Post>>> =
        posts.map { all ->
            DataResult.Success(
                all.filter { metroId == null || it.metroId == metroId }
                    .sortedByDescending { it.createdAtMillis ?: 0L }
                    .take(limit),
            )
        }

    override suspend fun createPost(
        type: PostType,
        text: String,
        metroId: String?,
        image: PickedImage?,
    ): DataResult<Unit> {
        posts.update { all ->
            listOf(
                Post(
                    id = "fake-post-${all.size + 1}",
                    authorUid = currentUid ?: "preview",
                    authorName = currentName,
                    type = type,
                    text = text,
                    metroId = metroId,
                    createdAtMillis = Clock.System.now().toEpochMilliseconds(),
                ),
            ) + all
        }
        return DataResult.Success(Unit)
    }

    override fun observeLikedPostIds(): Flow<Set<String>> = liked

    override fun observeSavedPostIds(): Flow<Set<String>> = saved

    override suspend fun setLiked(postId: String, liked: Boolean): DataResult<Unit> {
        this.liked.update { if (liked) it + postId else it - postId }
        posts.update { all ->
            all.map {
                if (it.id == postId) it.copy(likeCount = (it.likeCount + if (liked) 1 else -1).coerceAtLeast(0)) else it
            }
        }
        return DataResult.Success(Unit)
    }

    override suspend fun setSaved(postId: String, saved: Boolean): DataResult<Unit> {
        this.saved.update { if (saved) it + postId else it - postId }
        return DataResult.Success(Unit)
    }

    override fun observeComments(postId: String): Flow<DataResult<List<Comment>>> =
        comments.map { DataResult.Success(it[postId].orEmpty()) }

    override suspend fun addComment(postId: String, text: String, recommendedBusiness: Business?): DataResult<Unit> {
        comments.update { all ->
            val current = all[postId].orEmpty()
            all + (postId to current + Comment(
                id = "fake-comment-${current.size + 1}",
                authorUid = currentUid ?: "preview",
                authorName = currentName,
                text = text,
                recommendedBusinessId = recommendedBusiness?.id,
                recommendedBusinessName = recommendedBusiness?.name,
                createdAtMillis = Clock.System.now().toEpochMilliseconds(),
                isMine = true,
            ))
        }
        posts.update { all -> all.map { if (it.id == postId) it.copy(commentCount = it.commentCount + 1) else it } }
        return DataResult.Success(Unit)
    }

    override suspend fun updatePost(postId: String, type: PostType, text: String): DataResult<Unit> {
        posts.update { all -> all.map { if (it.id == postId) it.copy(type = type, text = text) else it } }
        return DataResult.Success(Unit)
    }

    override suspend fun deletePost(postId: String): DataResult<Unit> {
        posts.update { all -> all.filterNot { it.id == postId } }
        return DataResult.Success(Unit)
    }

    override suspend fun deleteComment(postId: String, commentId: String): DataResult<Unit> {
        comments.update { all -> all + (postId to all[postId].orEmpty().filterNot { it.id == commentId }) }
        posts.update { all -> all.map { if (it.id == postId) it.copy(commentCount = (it.commentCount - 1).coerceAtLeast(0)) else it } }
        return DataResult.Success(Unit)
    }
}

/** Seeded [OwnerRepository]; replies are forwarded onto the shared review state when provided. */
class FakeOwnerRepository(
    private val owned: List<Business> = emptyList(),
    private val reviewRepository: FakeReviewRepository? = null,
) : OwnerRepository {
    override fun observeOwnedBusinesses(): Flow<DataResult<List<Business>>> =
        flowOf(DataResult.Success(owned))

    override suspend fun replyToReview(reviewId: String, replyText: String): DataResult<Unit> {
        reviewRepository?.applyOwnerReply(reviewId, replyText)
        return DataResult.Success(Unit)
    }
}

/** Stateful [ChatRepository]: seeded threads; sends append and bump the inbox. Empty by default. */
class FakeChatRepository(
    seedConversations: List<Conversation> = emptyList(),
    seedMessages: Map<String, List<Message>> = emptyMap(),
    override val currentUid: String? = null,
    /** Discoverable members for Find your people / the people map (empty = nobody to discover). */
    seedPeople: List<diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile> = emptyList(),
) : ChatRepository {
    private val conversations = MutableStateFlow(seedConversations)
    private val messages = MutableStateFlow(seedMessages)

    override fun observeConversations(): Flow<DataResult<List<Conversation>>> =
        conversations.map { all -> DataResult.Success(all.sortedByDescending { it.lastMessageAtMillis ?: 0L }) }

    override fun observeMessages(conversationId: String): Flow<DataResult<List<Message>>> =
        messages.map { DataResult.Success(it[conversationId].orEmpty()) }

    override suspend fun getConversation(conversationId: String): DataResult<Conversation> =
        conversations.value.firstOrNull { it.id == conversationId }?.let { DataResult.Success(it) }
            ?: DataResult.Failure(AppError.NotFound())

    override suspend fun ensureConversation(business: Business): DataResult<String> {
        val uid = currentUid ?: "preview"
        val id = "${uid}_${business.id}"
        if (conversations.value.none { it.id == id }) {
            conversations.update { all ->
                all + Conversation(
                    id = id,
                    businessId = business.id,
                    businessName = business.name,
                    participantUids = listOf(uid, business.ownerUid ?: "fake-owner"),
                )
            }
        }
        return DataResult.Success(id)
    }

    override suspend fun ensureItemConversation(item: MarketplaceItem): DataResult<String> {
        val uid = currentUid ?: "preview"
        val id = "item_${item.id}_$uid"
        if (conversations.value.none { it.id == id }) {
            conversations.update { all ->
                all + Conversation(
                    id = id,
                    businessId = "",
                    businessName = item.title,
                    itemId = item.id,
                    participantUids = listOf(uid, item.sellerUid),
                )
            }
        }
        return DataResult.Success(id)
    }

    override suspend fun ensureDmConversation(other: diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile): DataResult<String> {
        val uid = currentUid ?: "preview"
        if (uid == other.uid) return DataResult.Failure(diana.soleil.design.iraniancanadienbazzar.core.common.AppError.Unknown())
        val id = Conversation.dmId(uid, other.uid)
        if (conversations.value.none { it.id == id }) {
            conversations.update { all ->
                all + Conversation(
                    id = id,
                    businessId = "",
                    businessName = other.displayName,
                    participantUids = listOf(uid, other.uid),
                    type = Conversation.TYPE_DM,
                    acceptedUids = listOf(uid),
                    createdBy = uid,
                )
            }
        }
        return DataResult.Success(id)
    }

    override suspend fun createGroupConversation(
        title: String,
        members: List<diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile>,
    ): DataResult<String> {
        val uid = currentUid ?: "preview"
        val id = "group_fake_${conversations.value.size + 1}"
        conversations.update { all ->
            all + Conversation(
                id = id,
                businessId = "",
                businessName = title,
                participantUids = listOf(uid) + members.map { it.uid },
                type = Conversation.TYPE_GROUP,
                acceptedUids = listOf(uid),
                createdBy = uid,
                participantNames = buildMap {
                    put(uid, "Me")
                    members.forEach { put(it.uid, it.displayName) }
                },
            )
        }
        return DataResult.Success(id)
    }

    override suspend fun acceptConversation(conversationId: String): DataResult<Unit> {
        val uid = currentUid ?: "preview"
        conversations.update { all ->
            all.map { if (it.id == conversationId) it.copy(acceptedUids = (it.acceptedUids + uid).distinct()) else it }
        }
        return DataResult.Success(Unit)
    }

    override suspend fun declineConversation(conversationId: String): DataResult<Unit> {
        conversations.update { all -> all.filterNot { it.id == conversationId } }
        return DataResult.Success(Unit)
    }

    override suspend fun leaveConversation(conversationId: String): DataResult<Unit> {
        // Single-user fake world: leaving removes the thread from the (only) caller's inbox.
        conversations.update { all -> all.filterNot { it.id == conversationId } }
        return DataResult.Success(Unit)
    }

    override suspend fun searchUsers(query: String): DataResult<List<diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile>> {
        val q = query.trim().lowercase()
        if (q.isEmpty()) return DataResult.Success(emptyList())
        val seed = listOf(
            diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile("fake-user-1", "Neda T.", "Toronto"),
            diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile("fake-user-2", "Marc L.", "Montreal"),
        )
        return DataResult.Success(seed.filter { it.displayName.lowercase().startsWith(q) && it.uid != currentUid })
    }

    private val discovery = MutableStateFlow<List<diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile>>(seedPeople)

    override fun observeDiscoverablePeople(intent: String?, limit: Int): Flow<DataResult<List<diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile>>> =
        discovery.map { all ->
            DataResult.Success(all.filter { it.discoverable && (intent == null || intent in it.intents) }.take(limit))
                as DataResult<List<diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile>>
        }

    override suspend fun myPublicProfile(): DataResult<diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile?> =
        DataResult.Success(discovery.value.firstOrNull { it.uid == (currentUid ?: "preview") })

    override suspend fun publicProfile(uid: String): DataResult<diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile?> =
        DataResult.Success(discovery.value.firstOrNull { it.uid == uid })

    override suspend fun setDiscoveryProfile(discoverable: Boolean, bio: String?, intents: List<String>): DataResult<Unit> {
        val uid = currentUid ?: "preview"
        discovery.update { all ->
            all.filterNot { it.uid == uid } + diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile(
                uid = uid, displayName = "Me", bio = bio, intents = intents, discoverable = discoverable,
            )
        }
        return DataResult.Success(Unit)
    }

    override suspend fun setMapVisibility(
        precision: diana.soleil.design.iraniancanadienbazzar.core.model.MapPrecision,
        lat: Double?,
        lng: Double?,
    ): DataResult<Unit> = DataResult.Success(Unit)

    override suspend fun setSocialProfile(
        city: String?,
        occupation: String?,
        hometown: String?,
        relationship: String?,
        languages: List<String>,
        interests: List<String>,
    ): DataResult<Unit> {
        val uid = currentUid ?: "preview"
        discovery.update { all ->
            val existing = all.firstOrNull { it.uid == uid }
                ?: diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile(uid = uid, displayName = "Me")
            all.filterNot { it.uid == uid } + existing.copy(
                city = city, occupation = occupation, hometown = hometown, relationship = relationship,
                languages = languages, interests = interests,
            )
        }
        return DataResult.Success(Unit)
    }

    override suspend fun setProfilePhoto(
        image: diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage,
    ): DataResult<String> = DataResult.Success("fake://profile-photo")

    /** In-memory gallery so the photo strip is exercisable in mock mode and in tests. */
    private val fakePhotos = MutableStateFlow<List<String>>(emptyList())

    override suspend fun addProfilePhoto(
        image: diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage,
    ): DataResult<String> {
        if (fakePhotos.value.size >= diana.soleil.design.iraniancanadienbazzar.core.data.MAX_PROFILE_PHOTOS) {
            return DataResult.Failure(AppError.Conflict())
        }
        val url = "fake://profile-photo-" + (fakePhotos.value.size + 1)
        fakePhotos.update { it + url }
        return DataResult.Success(url)
    }

    override suspend fun removeProfilePhoto(url: String): DataResult<Unit> {
        fakePhotos.update { list -> list.filterNot { it == url } }
        return DataResult.Success(Unit)
    }

    override suspend fun sendMessage(conversationId: String, text: String): DataResult<Unit> {
        val nowMillis = Clock.System.now().toEpochMilliseconds()
        messages.update { all ->
            val current = all[conversationId].orEmpty()
            all + (conversationId to current + Message(
                id = "fake-msg-${current.size + 1}",
                senderUid = currentUid ?: "preview",
                text = text,
                createdAtMillis = nowMillis,
                isMine = true,
            ))
        }
        conversations.update { all ->
            all.map {
                if (it.id == conversationId) it.copy(lastMessage = text, lastMessageAtMillis = nowMillis) else it
            }
        }
        return DataResult.Success(Unit)
    }
}

/** Stateful [ChannelRepository]: in-memory channels + posts for previews/dev fakes. */
class FakeChannelRepository(
    override val currentUid: String? = "fake-admin",
) : diana.soleil.design.iraniancanadienbazzar.core.data.ChannelRepository {

    private val channels = MutableStateFlow<Map<String, diana.soleil.design.iraniancanadienbazzar.core.model.Channel>>(emptyMap())
    private val posts = MutableStateFlow<Map<String, List<diana.soleil.design.iraniancanadienbazzar.core.model.ChannelPost>>>(emptyMap())

    override fun observeChannel(businessId: String): Flow<DataResult<diana.soleil.design.iraniancanadienbazzar.core.model.Channel?>> =
        channels.map { DataResult.Success(it[businessId]) as DataResult<diana.soleil.design.iraniancanadienbazzar.core.model.Channel?> }

    override suspend fun ensureChannel(business: Business): DataResult<Unit> {
        channels.update { all ->
            if (business.id in all) all
            else all + (business.id to diana.soleil.design.iraniancanadienbazzar.core.model.Channel(
                businessId = business.id, name = business.name, ownerUid = currentUid ?: "preview",
            ))
        }
        return DataResult.Success(Unit)
    }

    override fun observePosts(businessId: String, limit: Int): Flow<DataResult<List<diana.soleil.design.iraniancanadienbazzar.core.model.ChannelPost>>> =
        posts.map {
            DataResult.Success(it[businessId].orEmpty().take(limit))
                as DataResult<List<diana.soleil.design.iraniancanadienbazzar.core.model.ChannelPost>>
        }

    override suspend fun post(businessId: String, text: String): DataResult<Unit> {
        val nowMillis = Clock.System.now().toEpochMilliseconds()
        posts.update { all ->
            val current = all[businessId].orEmpty()
            all + (businessId to listOf(
                diana.soleil.design.iraniancanadienbazzar.core.model.ChannelPost(
                    id = "fake-chpost-${current.size + 1}", text = text, createdAtMillis = nowMillis,
                ),
            ) + current)
        }
        channels.update { all ->
            val channel = all[businessId] ?: return@update all
            all + (businessId to channel.copy(lastPostText = text.take(120), lastPostAtMillis = nowMillis))
        }
        return DataResult.Success(Unit)
    }

    override suspend fun channelsFor(businessIds: Collection<String>): DataResult<List<diana.soleil.design.iraniancanadienbazzar.core.model.Channel>> =
        DataResult.Success(
            businessIds.mapNotNull { channels.value[it] }.sortedByDescending { it.lastPostAtMillis ?: 0L },
        )
}

/** Stateful [AdminRepository]: seeded moderation queues; approve/reject/resolve mutate live. */
class FakeAdminRepository(
    seedPendingBusinesses: List<Business> = emptyList(),
    seedReports: List<Report> = emptyList(),
    seedPendingEvents: List<Event> = emptyList(),
    seedClaims: List<BusinessClaim> = emptyList(),
    seedUsers: List<UserAccount> = listOf(
        UserAccount(uid = "fake-admin", displayName = "Dev Admin", role = UserAccount.ROLE_ADMIN, metroId = "gta", preferredLanguage = "en"),
        UserAccount(uid = "fake-user-1", displayName = "Neda T.", metroId = "gta", preferredLanguage = "fa"),
        UserAccount(uid = "fake-user-2", displayName = "Marc L.", metroId = "montreal", preferredLanguage = "fr"),
    ),
    override val currentUid: String? = "fake-admin",
) : AdminRepository {
    private val pendingBusinesses = MutableStateFlow(seedPendingBusinesses)
    private val reports = MutableStateFlow(seedReports)
    private val pendingEvents = MutableStateFlow(seedPendingEvents)
    private val claims = MutableStateFlow(seedClaims)
    private val users = MutableStateFlow(seedUsers)

    override fun observePendingBusinesses(): Flow<DataResult<List<Business>>> =
        pendingBusinesses.map { DataResult.Success(it) }

    // Fake world has no Function to set the flag — the re-review queue simply starts empty.
    private val editedBusinesses = MutableStateFlow<List<Business>>(emptyList())

    override fun observeEditedBusinesses(): Flow<DataResult<List<Business>>> =
        editedBusinesses.map { DataResult.Success(it) }

    override fun observeAllBusinesses(): Flow<DataResult<List<Business>>> =
        pendingBusinesses.map { DataResult.Success(it) }

    override fun searchBusinessesByName(prefix: String, limit: Int): Flow<DataResult<List<Business>>> =
        pendingBusinesses.map { all -> DataResult.Success(all.filter { it.name.contains(prefix.trim(), ignoreCase = true) }.take(limit)) }

    override suspend fun setBusinessStatus(businessId: String, status: BusinessStatus, reason: String?): DataResult<Unit> {
        if (status != BusinessStatus.Pending) {
            pendingBusinesses.update { all -> all.filterNot { it.id == businessId } }
            editedBusinesses.update { all -> all.filterNot { it.id == businessId } }
        }
        return DataResult.Success(Unit)
    }

    override fun observeOpenReports(): Flow<DataResult<List<Report>>> =
        reports.map { all -> DataResult.Success(all.filter { it.status == ReportStatus.Open }) }

    override suspend fun resolveReport(reportId: String): DataResult<Unit> {
        reports.update { all -> all.map { if (it.id == reportId) it.copy(status = ReportStatus.Resolved) else it } }
        return DataResult.Success(Unit)
    }

    override fun observeListingRequests(): Flow<DataResult<List<diana.soleil.design.iraniancanadienbazzar.core.model.ListingRequest>>> =
        kotlinx.coroutines.flow.flowOf(DataResult.Success(emptyList()))

    override suspend fun resolveListingRequest(requestId: String): DataResult<Unit> = DataResult.Success(Unit)

    override fun observePendingEvents(): Flow<DataResult<List<Event>>> =
        pendingEvents.map { DataResult.Success(it) }

    override suspend fun setEventStatus(eventId: String, approved: Boolean, reason: String?): DataResult<Unit> {
        pendingEvents.update { all -> all.filterNot { it.id == eventId } }
        return DataResult.Success(Unit)
    }

    override fun observeClaims(): Flow<DataResult<List<BusinessClaim>>> =
        claims.map { DataResult.Success(it) }

    override suspend fun approveClaim(claim: BusinessClaim): DataResult<Unit> {
        claims.update { all -> all.map { if (it.id == claim.id) it.copy(status = ClaimReviewStatus.Approved) else it } }
        return DataResult.Success(Unit)
    }

    override suspend fun rejectClaim(claim: BusinessClaim): DataResult<Unit> {
        claims.update { all -> all.map { if (it.id == claim.id) it.copy(status = ClaimReviewStatus.Rejected) else it } }
        return DataResult.Success(Unit)
    }

    private val listingChanges =
        MutableStateFlow<List<diana.soleil.design.iraniancanadienbazzar.core.model.ListingChangeRequest>>(emptyList())

    override fun observePendingListingChanges():
        Flow<DataResult<List<diana.soleil.design.iraniancanadienbazzar.core.model.ListingChangeRequest>>> =
        listingChanges.map { DataResult.Success(it) }

    override suspend fun approveListingChange(
        request: diana.soleil.design.iraniancanadienbazzar.core.model.ListingChangeRequest,
    ): DataResult<Unit> {
        listingChanges.update { all -> all.filterNot { it.id == request.id } }
        return DataResult.Success(Unit)
    }

    override suspend fun rejectListingChange(
        request: diana.soleil.design.iraniancanadienbazzar.core.model.ListingChangeRequest,
        reason: String?,
    ): DataResult<Unit> {
        listingChanges.update { all -> all.filterNot { it.id == request.id } }
        return DataResult.Success(Unit)
    }

    private val pendingContent =
        MutableStateFlow<List<diana.soleil.design.iraniancanadienbazzar.core.model.PendingContentItem>>(emptyList())

    override fun observePendingContent():
        Flow<DataResult<List<diana.soleil.design.iraniancanadienbazzar.core.model.PendingContentItem>>> =
        pendingContent.map { DataResult.Success(it) }

    override suspend fun publishContent(
        item: diana.soleil.design.iraniancanadienbazzar.core.model.PendingContentItem,
    ): DataResult<Unit> {
        pendingContent.update { all -> all.filterNot { it.id == item.id } }
        return DataResult.Success(Unit)
    }

    override suspend fun discardContent(
        item: diana.soleil.design.iraniancanadienbazzar.core.model.PendingContentItem,
    ): DataResult<Unit> {
        pendingContent.update { all -> all.filterNot { it.id == item.id } }
        return DataResult.Success(Unit)
    }

    override suspend fun hideReview(reviewId: String): DataResult<Unit> = DataResult.Success(Unit)

    override suspend fun removePost(postId: String): DataResult<Unit> = DataResult.Success(Unit)

    override suspend fun removeMarketplaceItem(itemId: String): DataResult<Unit> = DataResult.Success(Unit)

    override suspend fun restoreReview(reviewId: String): DataResult<Unit> = DataResult.Success(Unit)

    override suspend fun restoreMarketplaceItem(itemId: String): DataResult<Unit> = DataResult.Success(Unit)

    private val moderationLog = MutableStateFlow<List<ModerationAction>>(emptyList())

    override suspend fun logModerationAction(
        action: String,
        targetType: String,
        targetId: String,
        reason: String?,
    ): DataResult<Unit> {
        moderationLog.update { log ->
            log + ModerationAction(
                id = "fake-${log.size}",
                actorUid = currentUid ?: "fake-admin",
                action = action,
                targetType = targetType,
                targetId = targetId,
                reason = reason,
                createdAtMillis = null,
            )
        }
        return DataResult.Success(Unit)
    }

    override fun observeModerationLog(): Flow<DataResult<List<ModerationAction>>> =
        moderationLog.map { DataResult.Success(it.asReversed()) as DataResult<List<ModerationAction>> }

    override fun observeMyRole(): Flow<String?> =
        users.map { all -> all.firstOrNull { it.uid == currentUid }?.role ?: UserAccount.ROLE_ADMIN }

    override fun observeUsers(): Flow<DataResult<List<UserAccount>>> =
        users.map { DataResult.Success(it) as DataResult<List<UserAccount>> }

    override suspend fun setUserRole(uid: String, role: String): DataResult<Unit> {
        users.value = users.value.map { if (it.uid == uid) it.copy(role = role) else it }
        return DataResult.Success(Unit)
    }

    override suspend fun sendCampaign(
        campaignId: String,
        metroId: String?,
        title: LocalizedText,
        body: LocalizedText,
        link: String,
        channel: CampaignChannel,
    ): DataResult<CampaignSendResult> = DataResult.Success(CampaignSendResult(sent = 42, capped = false))

    override fun observeGrowthDaily(): Flow<DataResult<List<DailyPlatformCounters>>> =
        flowOf(
            DataResult.Success(
                listOf(
                    DailyPlatformCounters("20260628", users = 2, businesses = 1),
                    DailyPlatformCounters("20260629", users = 4, businesses = 2),
                    DailyPlatformCounters("20260630", users = 3, businesses = 0),
                    DailyPlatformCounters("20260701", users = 6, businesses = 3),
                    DailyPlatformCounters("20260702", users = 5, businesses = 2),
                    DailyPlatformCounters("20260703", users = 8, businesses = 4),
                    DailyPlatformCounters("20260704", users = 7, businesses = 3),
                ),
            ),
        )
}

/** [UserProfileRepository] double; optionally seeded with a profile (mock mode). */
class FakeUserProfileRepository(
    seedProfile: UserProfile? = null,
) : UserProfileRepository {
    override suspend fun getPreferences(uid: String): DataResult<UserPreferences?> =
        DataResult.Success(null)

    override suspend fun getRole(uid: String): DataResult<String?> = DataResult.Success(null)

    override suspend fun syncPreferences(
        user: AuthUser,
        preferredLanguage: String,
        metroId: String?,
        savedBusinessIds: Set<String>,
        savedNewsIds: Set<String>,
        interests: Set<String>,
    ): DataResult<Unit> = DataResult.Success(Unit)

    private val profile = MutableStateFlow(seedProfile)

    override fun observeProfile(): Flow<DataResult<UserProfile?>> =
        profile.map { DataResult.Success(it) }

    override suspend fun updateProfile(displayName: String, bio: String): DataResult<Unit> {
        profile.value = UserProfile(
            displayName = displayName.ifBlank { null },
            bio = bio.ifBlank { null },
            createdAtMillis = profile.value?.createdAtMillis,
            pushEnabled = profile.value?.pushEnabled ?: true,
            gemPushEnabled = profile.value?.gemPushEnabled ?: true,
            emailMarketingConsented = profile.value?.emailMarketingConsented ?: false,
        )
        return DataResult.Success(Unit)
    }

    override suspend fun setPushEnabled(enabled: Boolean): DataResult<Unit> {
        profile.value = (profile.value ?: UserProfile(null, null, null)).copy(pushEnabled = enabled)
        return DataResult.Success(Unit)
    }

    override suspend fun setGemPushEnabled(enabled: Boolean): DataResult<Unit> {
        profile.value = (profile.value ?: UserProfile(null, null, null)).copy(gemPushEnabled = enabled)
        return DataResult.Success(Unit)
    }

    override suspend fun setEmailMarketingConsent(consented: Boolean): DataResult<Unit> {
        profile.value = (profile.value ?: UserProfile(null, null, null))
            .copy(emailMarketingConsented = consented)
        return DataResult.Success(Unit)
    }

    override suspend fun createSubscriptionCheckout(planId: String): DataResult<String> =
        DataResult.Success("https://checkout.stripe.com/fake/sub/$planId")

    override suspend fun createBillingPortalSession(): DataResult<String> =
        DataResult.Success("https://billing.stripe.com/fake/portal")
}

/** Stateful [BlockRepository]; optionally seeded (mock mode). */
class FakeBlockRepository(
    seed: List<BlockedUser> = emptyList(),
) : BlockRepository {
    private val blocked = MutableStateFlow(seed)

    override fun observeBlockedUids(): Flow<Set<String>> =
        blocked.map { all -> all.map { it.uid }.toSet() }

    override fun observeBlockedUsers(): Flow<List<BlockedUser>> = blocked

    override suspend fun block(targetUid: String, targetName: String): DataResult<Unit> {
        blocked.update { all -> all.filterNot { it.uid == targetUid } + BlockedUser(targetUid, targetName) }
        return DataResult.Success(Unit)
    }

    override suspend fun unblock(targetUid: String): DataResult<Unit> {
        blocked.update { all -> all.filterNot { it.uid == targetUid } }
        return DataResult.Success(Unit)
    }
}

/** Stateful [diana.soleil.design.iraniancanadienbazzar.core.data.DealsRepository]: seeded demo deals. */
class FakeDealsRepository(
    seed: List<Deal> = emptyList(),
) : diana.soleil.design.iraniancanadienbazzar.core.data.DealsRepository {
    private val deals = MutableStateFlow(seed)

    override fun observeActiveDeals(limit: Int): Flow<DataResult<List<Deal>>> =
        deals.map { all -> DataResult.Success(all.filter { it.visible }.take(limit)) as DataResult<List<Deal>> }

    override fun observeDealsForBusiness(businessId: String): Flow<DataResult<List<Deal>>> =
        deals.map { all -> DataResult.Success(all.filter { it.businessId == businessId }) as DataResult<List<Deal>> }

    override suspend fun createDeal(
        businessId: String,
        businessName: String,
        title: diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText,
        details: diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText?,
        endsAtMillis: Long,
    ): DataResult<Unit> {
        deals.update { all ->
            all + Deal(
                id = "fake-deal-${all.size + 1}", businessId = businessId, businessName = businessName,
                title = title, details = details, endsAtMillis = endsAtMillis,
            )
        }
        return DataResult.Success(Unit)
    }

    override suspend fun deleteDeal(dealId: String): DataResult<Unit> {
        deals.update { all -> all.filterNot { it.id == dealId } }
        return DataResult.Success(Unit)
    }
}
